import {
  RECOMMENDATION_DRAFT_TTL_MS,
  createRecommendationDraft,
  normalizeRecommendationDraft,
  type RecommendationDraft,
} from "./recommendationDraft.ts";

export const RECOMMENDATION_DRAFT_STORAGE_KEY = "beautinow.productSearch.recommendationDraft.v1";

export type RecommendationDraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserStorage(): RecommendationDraftStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
export function loadRecommendationDraft(
  storage: RecommendationDraftStorage | null = browserStorage(),
  now = Date.now(),
): RecommendationDraft {
  if (!storage) return createRecommendationDraft(now);
  try {
    const raw = storage.getItem(RECOMMENDATION_DRAFT_STORAGE_KEY);
    if (!raw) return createRecommendationDraft(now);
    const normalized = normalizeRecommendationDraft(JSON.parse(raw), now);
    if (normalized) return normalized;
    storage.removeItem(RECOMMENDATION_DRAFT_STORAGE_KEY);
  } catch {
    try {
      storage.removeItem(RECOMMENDATION_DRAFT_STORAGE_KEY);
    } catch {
      // Storage can be unavailable in restricted WebViews; keep the page usable in memory.
    }
  }
  return createRecommendationDraft(now);
}

export function saveRecommendationDraft(
  draft: RecommendationDraft,
  storage: RecommendationDraftStorage | null = browserStorage(),
  now = Date.now(),
): RecommendationDraft {
  const next: RecommendationDraft = {
    ...draft,
    updatedAt: now,
    expiresAt: now + RECOMMENDATION_DRAFT_TTL_MS,
  };
  if (storage) {
    try {
      storage.setItem(RECOMMENDATION_DRAFT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Recommendation still works for the current screen if local storage is unavailable.
    }
  }
  return next;
}

export function updateRecommendationDraft(
  update: (draft: RecommendationDraft) => RecommendationDraft,
  storage: RecommendationDraftStorage | null = browserStorage(),
  now = Date.now(),
): RecommendationDraft {
  return saveRecommendationDraft(update(loadRecommendationDraft(storage, now)), storage, now);
}

export function clearRecommendationDraft(storage: RecommendationDraftStorage | null = browserStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(RECOMMENDATION_DRAFT_STORAGE_KEY);
  } catch {
    // Clearing is best-effort in restricted WebViews.
  }
}
